import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '../types';
import { EllipsisVerticalIcon, PencilSquareIcon, TrashIcon, CheckIcon, TagIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Checkbox } from '@headlessui/react';
import { getCategoryColor } from '../services/database';
import CategoryModal from './CategoryModal';
import DueDateSelector from './DueDateSelector';

interface SortableTaskItemProps {
  task: Task;
  onToggle: (id: number) => Promise<void>;
  onDelete: (id: number) => void;
  isActive: boolean;
  onSelect: (id: number | null) => void;
  onUpdateTask: (id: number, text: string, category?: string) => Promise<void>;
  editingTaskId: number | null;
  onStartEditing: (taskId: number, currentText: string, currentCategory?: string, currentDueDate?: Date | null) => void;
  onCategoryAdded?: () => void;
  onCategoryRenamed?: (oldName: string, newName: string) => Promise<void>;
  onEditSubmit: (e: React.FormEvent) => Promise<void>;
  onCancelEdit: () => void;
  currentEditingText: string;
  onEditingTextChange: (text: string) => void;
  currentEditingCategory?: string;
  onEditingCategoryChange: (category?: string) => void;
  currentEditingDueDate?: Date | null;
  onEditingDueDateChange: (date: Date | null) => void;
}

const SortableTaskItemBase: React.FC<SortableTaskItemProps> = ({
  task,
  onToggle,
  onDelete,
  isActive,
  onSelect,
  onUpdateTask,
  editingTaskId,
  onStartEditing,
  onCategoryAdded,
  onCategoryRenamed,
  onEditSubmit,
  onCancelEdit,
  currentEditingText,
  onEditingTextChange,
  currentEditingCategory,
  onEditingCategoryChange,
  currentEditingDueDate,
  onEditingDueDateChange,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Determine if THIS task is the one being edited
  const isEditing = editingTaskId === task.id;

  // No longer need popup state and refs since using modal
  const editContainerRef = useRef<HTMLDivElement | null>(null); // Ref for the main container
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // --- dnd-kit Hook ---
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });

  // --- Ref combining for dnd-kit and local ref ---
  const handleSetRef = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    editContainerRef.current = node;
  }, [setNodeRef]); // Dependency on dnd-kit's setter

  // Effect to close action menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu &&
          menuRef.current &&
          buttonRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          !buttonRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      const el = editTextareaRef.current;
      el.focus();
      autoGrow(el);
      const len = el.value.length;
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = len;
      });
    }
  }, [isEditing]);

  useEffect(() => {
    autoGrow(editTextareaRef.current);
  }, [currentEditingText]);



  const style = {
    transform: CSS.Transform.toString(transform),
    // Only apply transition during active drag, not after drop (prevents jolt)
    transition: isDragging ? transition : undefined,
    zIndex: isDragging || showMenu || isEditing ? 50 : 1,
    position: 'relative' as const,
    opacity: 1,
    backgroundColor: isDragging 
      ? document.documentElement.classList.contains('dark') ? '#1e1e1e' : 'white' 
      : undefined,
    boxShadow: isDragging 
      ? document.documentElement.classList.contains('dark') ? '0 0 10px rgba(255, 255, 255, 0.1)' : '0 0 10px rgba(0, 0, 0, 0.1)' 
      : undefined,
    cursor: isEditing ? 'default' : 'grab',
    touchAction: isEditing ? 'auto' : 'none',
  };

  const handleStartEdit = () => {
                      onStartEditing(task.id, task.text, task.category, task.dueDate);
    setShowMenu(false);
  };

  // Get category color
  const getCategoryStyle = (category: string | undefined) => {
    const color = getCategoryColor(category || '');
    
    // Map color strings to Tailwind classes to avoid purging
    const bgClasses = {
      blue: 'bg-blue-100 dark:bg-blue-900/20',
      green: 'bg-green-100 dark:bg-green-900/20',
      red: 'bg-red-100 dark:bg-red-900/20',
      purple: 'bg-purple-100 dark:bg-purple-900/20',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/20',
      gray: 'bg-gray-100 dark:bg-gray-900/20',
    };
    
    const textClasses = {
      blue: 'text-blue-700 dark:text-blue-300',
      green: 'text-green-700 dark:text-green-300',
      red: 'text-red-700 dark:text-red-300',
      purple: 'text-purple-700 dark:text-purple-300',
      yellow: 'text-yellow-700 dark:text-yellow-300',
      gray: 'text-gray-700 dark:text-gray-300',
    };
    
    return {
      bg: bgClasses[color as keyof typeof bgClasses] || bgClasses.gray,
      text: textClasses[color as keyof typeof textClasses] || textClasses.gray,
    };
  };
  
  // Get category style for this task
  const categoryStyle = task.category ? getCategoryStyle(task.category) : null;

  return (
    <div
      ref={handleSetRef}
      style={style}
      {...(isEditing ? {} : { ...attributes, ...listeners })}
      className={`group relative flex items-center rounded-lg border p-3 mb-2 transition-colors duration-200 ${
        isActive && task.isToday && !task.completed
          ? 'border-2 border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-[#1e3a5a]'
          : task.completed 
            ? 'border border-gray-200 bg-gray-100 dark:bg-dark-hover dark:border-dark-border'
            : 'border border-gray-200 bg-white dark:bg-dark-card dark:border-dark-border'
      }`}
      onClick={(e) => {
        if (task.completed) return; // Skip completed tasks
        if (!(e.target as HTMLElement).closest('input[type="checkbox"]') && 
            !(e.target as HTMLElement).closest('button') &&
            !(e.target as HTMLElement).closest('textarea') &&
            !isEditing) {
          onSelect(task.id);
        }
      }}
    >
      <div className={`flex-1 flex items-center min-w-0 relative ${!isEditing ? '' : ''}`}> {/* Adjust spacing */}
        {/* Animated Checkbox - Only show when NOT editing */}
        { !isEditing && (
          <div
            className="absolute left-0 top-1 z-10"
            onClick={(e) => {
              // Prevent checkbox clicks from bubbling and triggering parent onSelect.
              e.stopPropagation();
            }}
          >
            <Checkbox
              style={{ cursor: 'auto' }}
              checked={task.completed}
              onChange={(checked: boolean) => {
                onToggle(task.id);
                if (!task.completed && isActive) {
                  onSelect(null);
                }
              }}
              className={`${task.completed ? 'bg-white border-gray-300 hover:border-blue-600 dark:bg-dark-card dark:border-dark-border dark:hover:border-blue-400' : 'bg-white dark:bg-dark-card border-gray-300 dark:border-dark-border hover:border-blue-600 dark:hover:border-blue-400'} cursor-pointer relative inline-flex h-4 w-4 items-center justify-center rounded border transition-all duration-[400ms] ease-in-out focus:outline-none focus:ring-0 transform group-hover:translate-x-0 -translate-x-6 opacity-0 group-hover:opacity-100`}
            >
              <span className="sr-only">Complete task</span>
              {task.completed && <CheckIcon className="h-4 w-4 text-black dark:text-white" strokeWidth={3} />}
            </Checkbox>
          </div>
        )} 
        {/* Editing UI or Display UI */}
        {isEditing ? (
          <div className="flex-1 flex flex-col space-y-2">
            {/* Task Name Input */}
            <textarea
              ref={editTextareaRef}
              rows={1}
              value={currentEditingText}
              onChange={(e) => {
                onEditingTextChange(e.target.value);
                autoGrow(e.currentTarget);
              }}
              onInput={(e) => autoGrow(e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onEditSubmit(e);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelEdit();
                }
              }}
              className="w-full border-0 focus:ring-0 p-0 text-gray-900 dark:text-dark-primary bg-transparent placeholder-gray-500 dark:placeholder-dark-secondary text-base whitespace-pre-wrap break-words resize-none overflow-hidden leading-relaxed"
            />

            {/* Container for category selector and buttons */}
            <div className="relative mt-1">
              {/* Action Buttons Row */} 
              <div className="flex justify-between items-center mt-1">
                {/* Left side: Tag button, Calendar button */}
                <div className="flex items-center space-x-1 min-w-0">
                  <CategoryModal
                    selectedCategory={currentEditingCategory}
                    onChange={(category: string | undefined) => {
                      onEditingCategoryChange(category);
                    }}
                    onCategoryRenamed={async (oldName: string, newName: string) => {
                      if (currentEditingCategory === oldName) {
                        onEditingCategoryChange(newName);
                      }
                      if (onCategoryRenamed) {
                        await onCategoryRenamed(oldName, newName);
                      }
                    }}
                  />
                  
                  <DueDateSelector
                    selectedDate={currentEditingDueDate || null}
                    onChange={onEditingDueDateChange}
                  />
                </div>

                {/* Right side: Cancel and Save buttons */}
                <div className="flex space-x-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCancelEdit(); }}
                    className="px-3 py-1 text-sm text-gray-600 dark:text-dark-secondary hover:text-gray-800 dark:hover:text-dark-primary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEditSubmit(e); }}
                    disabled={!currentEditingText.trim()}
                    className="px-3 py-1 bg-blue-500 dark:bg-blue-600 text-white text-sm rounded hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-0 transition-all duration-[400ms] ease-in-out transform translate-x-0 group-hover:translate-x-7">
            {/* Task Name */}
            <div className="flex items-center">
              <div 
                className={`break-words pr-7 ${
                  task.completed ? 'text-gray-500 dark:text-dark-secondary line-through' : 'text-gray-900 dark:text-dark-primary'
                }`}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {task.text}
              </div>
            </div>
              
              {/* Category Badge, Time Spent, and Due Date Row */}
              {(task.category || task.timeSpent > 0 || task.dueDate) && (
              <div className="flex items-center space-x-2 mt-1">
                {task.category && (
                  <span 
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${categoryStyle?.bg} ${categoryStyle?.text}`}
                  >
                    <TagIcon className="h-3 w-3 mr-1" />
                    {task.category}
                  </span>
                )}
                
                {task.dueDate && (
                  <span 
                    className={`inline-flex items-center text-xs font-medium ${getDueDateStyle(task.dueDate).bg} ${getDueDateStyle(task.dueDate).text} ${getDueDateStyle(task.dueDate).isColored ? 'px-2 py-0.5 rounded' : ''}`}
                  >
                    <CalendarIcon className="h-3 w-3 mr-1" />
                    {formatDueDate(task.dueDate)}
                  </span>
                )}
                
                {task.timeSpent > 0 && (
                  <span 
                    className="inline-flex items-center text-xs font-medium text-gray-500 dark:text-gray-400"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {formatTimeSpent(task.timeSpent)}
                  </span>
                )}
                </div>
              )}
          </div>
        )}
      </div>
      
      {/* Only show Ellipsis Menu when NOT editing */}
      { !isEditing && (
        <div className="relative ml-3">
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="text-gray-400 hover:text-gray-600 dark:text-dark-secondary dark:hover:text-dark-primary focus:outline-none p-1 rounded-full hover:bg-gray-50 dark:hover:bg-dark-hover"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
          {showMenu && (
            <div
              ref={menuRef}
              className="absolute right-0 top-10 mt-1 w-48 bg-white dark:bg-dark-card rounded-md shadow-lg ring-1 ring-black ring-opacity-10 dark:ring-white dark:ring-opacity-20 focus:outline-none z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                <button
                  onClick={(e) => { e.stopPropagation(); handleStartEdit(); }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-hover"
                >
                  <PencilSquareIcon className="mr-3 h-4 w-4 text-gray-500 dark:text-gray-300" aria-hidden="true" />
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(task.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-dark-hover"
                >
                  <TrashIcon className="h-4 w-4 mr-3 text-red-600 dark:text-red-400" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formatTimeSpent = (seconds: number): string => {
  if (!seconds || isNaN(seconds) || seconds <= 0) return "";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return minutes > 0 ? `${minutes}m` : "< 1m";
};

const getDueDateStyle = (dueDate: Date) => {
  const today = new Date();
  const due = new Date(dueDate);
  
  // Reset time parts for accurate day comparison
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  
  const timeDiff = due.getTime() - today.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  if (daysDiff < 0) {
    // Overdue - red (inverted colors)
    return { bg: 'bg-red-700 dark:bg-red-300', text: 'text-red-100 dark:text-red-900', isColored: true };
  } else if (daysDiff === 0) {
    // Due today - red (inverted colors)
    return { bg: 'bg-red-700 dark:bg-red-300', text: 'text-red-100 dark:text-red-900', isColored: true };
  } else if (daysDiff <= 2) {
    // Within 2 days - yellow
    return { bg: 'bg-yellow-500', text: 'text-white dark:text-yellow-900', isColored: true };
  } else {
    // More than 2 days - gray (normal text, no badge styling)
    return { bg: '', text: 'text-gray-700 dark:text-gray-300', isColored: false };
  }
};

const formatDueDate = (dueDate: Date): string => {
  const currentYear = new Date().getFullYear();
  const dueYear = dueDate.getFullYear();
  
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    ...(dueYear !== currentYear && { year: 'numeric' })
  };
  
  return dueDate.toLocaleDateString('en-US', options);
};

const SortableTaskItem = memo(SortableTaskItemBase, (prev, next) => {
  const tPrev = prev.task;
  const tNext = next.task;

  const taskEqual =
    tPrev === tNext ||
    (tPrev.id === tNext.id &&
      tPrev.text === tNext.text &&
      tPrev.completed === tNext.completed &&
      tPrev.isToday === tNext.isToday &&
      tPrev.category === tNext.category &&
      tPrev.dueDate?.toString() === tNext.dueDate?.toString() &&
      tPrev.timeSpent === tNext.timeSpent &&
      tPrev.todayOrder === tNext.todayOrder &&
      tPrev.upcomingOrder === tNext.upcomingOrder &&
      tPrev.completedAt?.toString() === tNext.completedAt?.toString());

  return (
    taskEqual &&
    prev.isActive === next.isActive &&
    prev.editingTaskId === next.editingTaskId &&
    prev.currentEditingText === next.currentEditingText &&
    prev.currentEditingCategory === next.currentEditingCategory &&
    prev.currentEditingDueDate?.toString() === next.currentEditingDueDate?.toString()
  );
});

export default SortableTaskItem;